import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { useCelleraTheme } from "theme/useCelleraTheme";

interface RenameFileModalProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  loading?: boolean;
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({
  open,
  currentName,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const { colors } = useCelleraTheme();
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open) {
      setNewName("");
    }
  }, [open]);

  const handleConfirm = () => {
    const trimmed = newName.trim();
    if (trimmed) {
      onConfirm(trimmed);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 4, p: 1 } },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          color: colors.text.primary,
        }}
      >
        Rename File
        <IconButton onClick={onClose} disabled={loading} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 3 }}
        >
          You are trying to rename file, please enter the new file name down
          below and click &quot;Update&quot;
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 1 }}
        >
          Current name
        </Typography>
        <TextField
          fullWidth
          value={currentName}
          disabled
          size="small"
          sx={{ mb: 2 }}
        />

        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, mb: 1 }}
        >
          New name
        </Typography>
        <TextField
          fullWidth
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New name"
          size="small"
          autoFocus
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newName.trim()) {
              handleConfirm();
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: "24px",
            px: 3,
            color: colors.text.primary,
            borderColor: colors.border.main,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !newName.trim()}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: "24px",
            px: 3,
            bgcolor: colors.primary.main,
            "&:hover": { bgcolor: colors.primary.dark },
          }}
        >
          {loading ? "Processing..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameFileModal;
